import { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

/**
 * Custom hook for FLIP animation of compound comparison
 * Animates compound structures from their source positions to comparison view
 *
 * @param {Object} options - Configuration options
 * @param {React.RefObject} options.sourceRef1 - Reference to first source element
 * @param {React.RefObject} options.sourceRef2 - Reference to second source element
 * @param {React.RefObject} options.containerRef - Reference to comparison container
 * @param {React.RefObject} options.image1Ref - Reference to first destination image
 * @param {React.RefObject} options.image2Ref - Reference to second destination image
 * @param {boolean} options.isReady - Whether content is ready to animate (e.g., images loaded)
 * @param {Function} options.renderClone1 - Function to render first animation clone
 * @param {Function} options.renderClone2 - Function to render second animation clone
 * @param {Function} options.onClose - Callback when comparison is closed
 * @param {string} options.entity1Id - First entity ID for debugging (optional)
 * @param {string} options.entity2Id - Second entity ID for debugging (optional)
 *
 * @returns {Object} Animation state and handlers
 */
export const useCompoundFlipAnimation = ({
  sourceRef1,
  sourceRef2,
  containerRef,
  image1Ref,
  image2Ref,
  isReady = true,
  renderClone1,
  renderClone2,
  onClose,
  entity1Id,
  entity2Id
}) => {
  const [isAnimating, setIsAnimating] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);
  const [imagesOpacity, setImagesOpacity] = useState(0);
  const [clonesOpacity, setClonesOpacity] = useState(1);
  const [animationClones, setAnimationClones] = useState(null);
  const hasAnimated = useRef(false);
  const measuredHeight = useRef(null);

  // Handle closing animation
  const handleClose = () => {
    if (isClosing) {
      return;
    }

    // First, capture the current height as a fixed number (if it's 'auto')
    if (containerRef.current && containerHeight === 'auto') {
      const currentHeight = containerRef.current.offsetHeight;
      setContainerHeight(currentHeight);

      // Use requestAnimationFrame to ensure the height is set before starting close animation
      requestAnimationFrame(() => {
        setIsClosing(true);
        setImagesOpacity(0);

        // Then animate to 0 in the next frame
        requestAnimationFrame(() => {
          setContainerHeight(0);
        });
      });
    } else {
      // Height is already a number, proceed directly
      setIsClosing(true);
      setImagesOpacity(0);
      setContainerHeight(0);
    }

    // Remove component after animations complete
    setTimeout(() => {
      if (onClose) {
        onClose();
      }
    }, 400);
  };

  // Handle ESC key to close comparison
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [isClosing]);

  // FLIP animation effect
  useEffect(() => {
    // Only run once and only when content is ready
    if (hasAnimated.current || !isReady) {
      return;
    }

    // Wait for refs to be populated (they're set via ref callbacks after render)
    // Poll briefly since React commit phase may not have fired yet
    let attempts = 0;
    const maxAttempts = 5;

    const checkRefsTimer = setInterval(() => {
      attempts++;

      // sourceRef1/sourceRef2 are ref objects — need .current to be a DOM element
      const source1Ready = sourceRef1?.current instanceof HTMLElement;
      const source2Ready = sourceRef2?.current instanceof HTMLElement;
      const dest1Ready = !!image1Ref.current;
      const dest2Ready = !!image2Ref.current;

      if (!sourceRef1 || !sourceRef2) {
        // No refs at all (hideFirstLast boundary case) — skip animation
        clearInterval(checkRefsTimer);
        setIsAnimating(false);
        setImagesOpacity(1);
        setContainerHeight('auto');
        return;
      }

      if (!source1Ready || !source2Ready || !dest1Ready || !dest2Ready) {
        if (attempts >= maxAttempts) {
          // Refs never resolved — show images without animation
          clearInterval(checkRefsTimer);
          setIsAnimating(false);
          setImagesOpacity(1);
          setContainerHeight('auto');
        }
        return;
      }

      clearInterval(checkRefsTimer);
      hasAnimated.current = true;

      // Find the animation overlay container
      const animationOverlay = document.getElementById('ev-ch-animation-overlay');
      if (!animationOverlay) {
        setIsAnimating(false);
        setImagesOpacity(1);
        setContainerHeight('auto');
        return;
      }

      let timeoutId = null;

      // Wait a frame to ensure images have rendered
      requestAnimationFrame(() => {
        if (!image1Ref.current || !image2Ref.current) {
          setIsAnimating(false);
          setImagesOpacity(1);
          setContainerHeight('auto');
          return;
        }

      // Find the source containers and images
      const sourceElement1 = sourceRef1.current;
      const sourceElement2 = sourceRef2.current;
      const sourceContainer1 = sourceElement1.querySelector('.ev-ch-fgm-node-img') || sourceElement1;
      const sourceContainer2 = sourceElement2.querySelector('.ev-ch-fgm-node-img') || sourceElement2;

      const sourceImg1 = sourceContainer1.querySelector('img') ||
                         sourceContainer1.querySelector('.compound-structure-image') ||
                         sourceContainer1;
      const sourceImg2 = sourceContainer2.querySelector('img') ||
                         sourceContainer2.querySelector('.compound-structure-image') ||
                         sourceContainer2;

      // Get destination images
      const destImg1 = image1Ref.current.querySelector('img') ||
                       image1Ref.current.querySelector('svg') ||
                       image1Ref.current;
      const destImg2 = image2Ref.current.querySelector('img') ||
                       image2Ref.current.querySelector('svg') ||
                       image2Ref.current;

      // Get source and destination positions
      const source1Rect = sourceImg1.getBoundingClientRect();
      const source2Rect = sourceImg2.getBoundingClientRect();
      const dest1Rect = destImg1.getBoundingClientRect();
      const dest2Rect = destImg2.getBoundingClientRect();

      // Get overlay position for relative positioning
      const overlayRect = animationOverlay.getBoundingClientRect();

      // Calculate transforms
      const scale1 = Math.min(dest1Rect.width / source1Rect.width, dest1Rect.height / source1Rect.height);
      const scale2 = Math.min(dest2Rect.width / source2Rect.width, dest2Rect.height / source2Rect.height);

      const translateX1 = dest1Rect.left - source1Rect.left;
      const translateY1 = dest1Rect.top - source1Rect.top;
      const translateX2 = dest2Rect.left - source2Rect.left;
      const translateY2 = dest2Rect.top - source2Rect.top;

      // Measure and set container height
      if (!measuredHeight.current && containerRef.current) {
        measuredHeight.current = containerRef.current.scrollHeight;
      }
      setContainerHeight(measuredHeight.current);

      // Create animation clones
      const clones = (
        <>
          <div
            className="compound-animation-clone"
            style={{
              position: 'absolute',
              left: source1Rect.left - overlayRect.left,
              top: source1Rect.top - overlayRect.top,
              width: source1Rect.width,
              height: source1Rect.height,
              transform: 'translate(0, 0) scale(1)',
              transformOrigin: 'top left',
              transition: 'none',
              pointerEvents: 'all',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: clonesOpacity
            }}
            ref={(el) => {
              if (el) {
                el.offsetHeight;
                requestAnimationFrame(() => {
                  el.style.transition = 'transform 0.5s cubic-bezier(0.4, 0.0, 0.2, 1), opacity 0.3s ease-out';
                  el.style.transform = `translate(${translateX1}px, ${translateY1}px) scale(${scale1})`;
                });
              }
            }}
          >
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {renderClone1 && renderClone1()}
            </div>
          </div>
          <div
            className="compound-animation-clone"
            style={{
              position: 'absolute',
              left: source2Rect.left - overlayRect.left,
              top: source2Rect.top - overlayRect.top,
              width: source2Rect.width,
              height: source2Rect.height,
              transform: 'translate(0, 0) scale(1)',
              transformOrigin: 'top left',
              transition: 'none',
              pointerEvents: 'all',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: clonesOpacity
            }}
            ref={(el) => {
              if (el) {
                el.offsetHeight;
                requestAnimationFrame(() => {
                  el.style.transition = 'transform 0.5s cubic-bezier(0.4, 0.0, 0.2, 1), opacity 0.3s ease-out';
                  el.style.transform = `translate(${translateX2}px, ${translateY2}px) scale(${scale2})`;
                });
              }
            }}
          >
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {renderClone2 && renderClone2()}
            </div>
          </div>
        </>
      );

      setAnimationClones(clones);

      // After animation completes, crossfade
      timeoutId = setTimeout(() => {
        setClonesOpacity(0);
        setImagesOpacity(1);

        setTimeout(() => {
          setAnimationClones(null);
          setIsAnimating(false);
          setContainerHeight('auto');
        }, 300);
      }, 500);
      }); // End of requestAnimationFrame
    }, 16); // End of checkRefsTimer — poll each frame

    return () => {
      clearInterval(checkRefsTimer);
    };
  }, [isReady]);

  // Render portal for animation clones
  const renderAnimationPortal = () => {
    if (!animationClones) return null;

    const overlay = document.getElementById('ev-ch-animation-overlay');
    if (!overlay) return null;

    return ReactDOM.createPortal(animationClones, overlay);
  };

  return {
    isAnimating,
    isClosing,
    containerHeight,
    imagesOpacity,
    handleClose,
    renderAnimationPortal
  };
};
